// src/components/settings/HolidayCalendarPage.js
import React, { useState, useEffect, useCallback, useId } from "react";
import Icon from "../icons/Icon";
import Modal from "../modals/Modal";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { holidayService } from "../../services/holidayService";
import "../../styles/components/settings-pages.css";
import "../../styles/components/holiday-calendar.css";

const HolidayCalendarPage = () => {
  const { company, hasPermission } = useAuth();
  const canEdit = hasPermission('set:hol:edit');

  // Calendar state
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [initialHolidays, setInitialHolidays] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  // Company rates for pay display
  const [companyRates, setCompanyRates] = useState({
    regularHolidayRate: 2.0,
    specialHolidayRate: 1.3,
  });

  // Philippine holiday presets
  const philippinePresets = holidayService.getPhilippinePresets();

  // Month and day constants
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Load holidays and rates on mount and year change
  useEffect(() => {
    const loadData = async () => {
      if (!company?.id) return;

      try {
        setLoading(true);

        // Fetch holidays for year
        const holidaysData = await holidayService.getHolidaysByYear(company.id, selectedYear);
        setHolidays(holidaysData);
        setInitialHolidays(JSON.parse(JSON.stringify(holidaysData))); // Deep copy

        // Fetch company rates from settings/payrollRates
        const ratesDoc = await getDoc(
          doc(db, "companies", company.id, "settings", "payrollRates")
        );
        if (ratesDoc.exists()) {
          const ratesData = ratesDoc.data();
          setCompanyRates({
            regularHolidayRate: ratesData.regularHolidayRate || 0,
            specialHolidayRate: ratesData.specialHolidayRate || 0,
          });
        }
      } catch (error) {
        console.error("Error loading holiday data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, selectedYear]);

  // Check for unsaved changes
  const hasChanges = useCallback(() => {
    if (holidays.length !== initialHolidays.length) return true;

    const sortedCurrent = [...holidays].sort((a, b) => a.date.localeCompare(b.date));
    const sortedInitial = [...initialHolidays].sort((a, b) => a.date.localeCompare(b.date));

    return sortedCurrent.some((holiday, index) => {
      const initial = sortedInitial[index];
      if (!initial) return true;
      return holiday.name !== initial.name ||
             holiday.type !== initial.type ||
             holiday.date !== initial.date;
    });
  }, [holidays, initialHolidays]);

  // Calendar generation
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const generateCalendarDates = () => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const dates = [];

    for (let i = 0; i < firstDay; i++) {
      dates.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(day);
    }

    return dates;
  };

  // Get holiday for a specific date
  const getHolidayForDate = (day) => {
    if (!day) return null;
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return holidays.find(holiday => holiday.date === dateStr);
  };

  // Navigate months
  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  // Handle year change
  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    if (year >= 2020 && year <= 2030) {
      setSelectedYear(year);
    }
  };

  // Add a holiday
  const handleAddHoliday = (holidayData) => {
    const dateStr = `${selectedYear}-${String(holidayData.month + 1).padStart(2, "0")}-${String(holidayData.day).padStart(2, "0")}`;
    const newHoliday = {
      id: `holiday-${Date.now()}`,
      name: holidayData.name,
      type: holidayData.type,
      date: dateStr,
      isRecurring: holidayData.isRecurring || false,
    };

    const updatedHolidays = [...holidays, newHoliday].sort((a, b) => a.date.localeCompare(b.date));
    setHolidays(updatedHolidays);
    setShowAddModal(false);
  };

  // Edit a holiday
  const handleEditHoliday = (holidayData) => {
    const dateStr = `${selectedYear}-${String(holidayData.month + 1).padStart(2, "0")}-${String(holidayData.day).padStart(2, "0")}`;
    const updatedHolidays = holidays.map(h =>
      h.id === editingHoliday.id
        ? { ...h, name: holidayData.name, type: holidayData.type, date: dateStr, isRecurring: holidayData.isRecurring || false }
        : h
    ).sort((a, b) => a.date.localeCompare(b.date));

    setHolidays(updatedHolidays);
    setEditingHoliday(null);
  };

  // Delete a holiday
  const handleDeleteHoliday = (holidayId) => {
    setHolidays(holidays.filter(h => h.id !== holidayId));
  };

  // Apply Philippine presets
  const handleApplyPresets = (selectedPresets) => {
    const newHolidays = [];

    selectedPresets.forEach(presetName => {
      const preset = philippinePresets.find(p => p.name === presetName);
      if (!preset) return;

      if (preset.isRecurring && preset.recurringRule) {
        const date = holidayService.calculateRecurringDate(selectedYear, preset.recurringRule);
        if (date) {
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

          if (!holidays.some(h => h.date === dateStr)) {
            newHolidays.push({
              id: `holiday-${Date.now()}-${newHolidays.length}`,
              name: preset.name,
              type: preset.type,
              date: dateStr,
              isRecurring: preset.isRecurring,
              recurringRule: preset.recurringRule,
              isPreset: true,
            });
          }
        }
      }
    });

    if (newHolidays.length > 0) {
      const updatedHolidays = [...holidays, ...newHolidays].sort((a, b) => a.date.localeCompare(b.date));
      setHolidays(updatedHolidays);
    }

    setShowPresetModal(false);
  };

  // Cancel changes
  const handleCancel = () => {
    setHolidays(JSON.parse(JSON.stringify(initialHolidays)));
  };

  // Save holidays
  const handleSave = async () => {
    if (!company?.id) return;

    try {
      setSaving(true);

      // Get holidays that need to be deleted (in initial but not in current)
      const deletedHolidays = initialHolidays.filter(
        initial => !holidays.some(h => h.id === initial.id)
      );

      // Get holidays that are new or modified
      const upsertHolidays = holidays.filter(holiday => {
        const initial = initialHolidays.find(h => h.id === holiday.id);
        if (!initial) return true; // New holiday
        return holiday.name !== initial.name ||
               holiday.type !== initial.type ||
               holiday.date !== initial.date;
      });

      // Delete removed holidays
      for (const holiday of deletedHolidays) {
        if (!holiday.id.startsWith('holiday-')) {
          await holidayService.deleteHoliday(company.id, selectedYear, holiday.id);
        }
      }

      // Add/update holidays
      for (const holiday of upsertHolidays) {
        const initial = initialHolidays.find(h => h.id === holiday.id);
        if (initial && !initial.id.startsWith('holiday-')) {
          // Update existing holiday in Firestore
          await holidayService.updateHoliday(company.id, selectedYear, holiday.id, {
            name: holiday.name,
            type: holiday.type,
            date: holiday.date,
            isRecurring: holiday.isRecurring || false,
          });
        } else {
          // Create new holiday
          await holidayService.createHoliday(company.id, {
            name: holiday.name,
            type: holiday.type,
            date: holiday.date,
            isRecurring: holiday.isRecurring || false,
          });
        }
      }

      // Update initial state to reflect saved state
      setInitialHolidays(JSON.parse(JSON.stringify(holidays)));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (error) {
      console.error("Error saving holidays:", error);
    } finally {
      setSaving(false);
    }
  };

  const calendarDates = generateCalendarDates();

  if (loading) {
    return (
      <div>
        <div className="settings-loading">
          <div className="settings-loading-spinner"></div>
          <div className="settings-loading-text">Loading holiday calendar...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="settings-page-title">Holiday Calendar</h1>
      <p className="settings-page-subtitle">
        Manage holidays that affect time records and pay calculations
      </p>

      {/* Holiday Calendar Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon">
              <span role="img" aria-label="calendar"><Icon name="calendar" size={16} /></span>
            </div>
            <div>
              <h2 className="settings-card-title">Company Holidays</h2>
              <p className="settings-card-description">
                Set up Philippine and custom holidays for your company
              </p>
            </div>
          </div>
        </div>

        <div className="settings-card-body">
          {/* Year Selector and Actions */}
          <div className="holiday-controls">
            <div className="holiday-year-selector">
              <label>Year:</label>
              <select value={selectedYear} onChange={handleYearChange}>
                {[...Array(11)].map((_, i) => {
                  const year = 2020 + i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            {canEdit && (
              <div className="holiday-actions">
                <button
                  type="button"
                  className="settings-btn settings-btn-secondary settings-btn-sm"
                  onClick={() => setShowPresetModal(true)}
                >
                  <span>🇵🇭</span> Add Philippine Holidays
                </button>
                <button
                  type="button"
                  className="settings-btn settings-btn-primary settings-btn-sm"
                  onClick={() => setShowAddModal(true)}
                >
                  <span>+</span> Add Holiday
                </button>
              </div>
            )}
          </div>

          {/* Calendar View */}
          <div className="holiday-calendar">
            {/* Month Navigation */}
            <div className="calendar-navigation">
              <button
                type="button"
                className="calendar-nav-button"
                onClick={() => navigateMonth("prev")}
                aria-label="Previous month"
              >
                <Icon name="chevron-left" size={16} />
              </button>
              <h3 className="calendar-month-title">
                {monthNames[selectedMonth]} {selectedYear}
              </h3>
              <button
                type="button"
                className="calendar-nav-button"
                onClick={() => navigateMonth("next")}
              >
                <Icon name="chevron-right" size={16} />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
              {/* Day headers */}
              {daysOfWeek.map(day => (
                <div key={day} className="calendar-day-header">{day}</div>
              ))}

              {/* Calendar dates */}
              {calendarDates.map((day, index) => {
                const holiday = day ? getHolidayForDate(day) : null;
                return (
                  <div
                    key={index}
                    className={`calendar-date ${!day ? "calendar-date-empty" : ""} ${holiday ? `calendar-date-holiday calendar-date-${holiday.type}` : ""}`}
                    onClick={() => canEdit && day && !holiday && setShowAddModal({ day, month: selectedMonth })}
                  >
                    {day && (
                      <>
                        <span className="calendar-date-number">{day}</span>
                        {holiday && (
                          <div
                            className="calendar-holiday-info"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canEdit) return;
                              const holidayDate = new Date(holiday.date + "T00:00:00");
                              setEditingHoliday({
                                ...holiday,
                                day: holidayDate.getDate(),
                                month: holidayDate.getMonth()
                              });
                            }}
                          >
                            <span className="calendar-holiday-name">{holiday.name}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Holiday Legend */}
            <div className="holiday-legend">
              <div className="holiday-legend-item">
                <span className="holiday-legend-color holiday-legend-regular"></span>
                <span>Regular Holiday ({Math.round(companyRates.regularHolidayRate * 100)}% pay)</span>
              </div>
              <div className="holiday-legend-item">
                <span className="holiday-legend-color holiday-legend-special"></span>
                <span>Special Holiday ({Math.round(companyRates.specialHolidayRate * 100)}% pay)</span>
              </div>
              <div className="holiday-legend-item">
                <span className="holiday-legend-color holiday-legend-company"></span>
                <span>Company Holiday</span>
              </div>
            </div>
          </div>

          {/* Holiday List */}
          {holidays.length > 0 && (
            <div className="holiday-list">
              <h3>Holiday Summary ({holidays.length} holidays)</h3>
              <div className="holiday-list-items">
                {holidays.map(holiday => (
                  <div key={holiday.id} className={`holiday-list-item holiday-type-${holiday.type}`}>
                    <div className="holiday-list-info">
                      <span className="holiday-list-date">
                        {new Date(holiday.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                      <span className="holiday-list-name">{holiday.name}</span>
                      <span className={`holiday-list-type holiday-type-badge-${holiday.type}`}>
                        {holiday.type}
                      </span>
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        className="holiday-list-delete"
                        onClick={() => handleDeleteHoliday(holiday.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Floating Save Bar */}
      {canEdit && <div
        className={`settings-floating-save-bar${
          hasChanges() || saveSuccess ? " visible" : ""
        }${saveSuccess ? " saved" : ""}`}
      >
        <div className="settings-save-bar-content">
          <div
            className={`settings-save-bar-left${
              saveSuccess ? " success" : ""
            }`}
          >
            <span className="settings-save-bar-dot"></span>
            <span className="settings-save-bar-check"><Icon name="check" size={14} /></span>
            <span className="settings-save-bar-text">
              {saveSuccess ? "Changes saved" : "Unsaved changes"}
            </span>
          </div>
          {!saveSuccess && (
            <div className="settings-save-bar-actions">
              <button
                className="settings-btn settings-btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Discard
              </button>
              <button
                className="settings-btn settings-btn-primary"
                onClick={handleSave}
                disabled={!hasChanges() || saving}
              >
                {saving ? (
                  <>
                    <span className="settings-btn-spinner"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          )}
        </div>
      </div>}

      {/* Add/Edit Holiday Modal */}
      {canEdit && (showAddModal || editingHoliday) && (
        <HolidayModal
          holiday={editingHoliday}
          initialDate={showAddModal}
          companyRates={companyRates}
          onSave={editingHoliday ? handleEditHoliday : handleAddHoliday}
          onClose={() => {
            setShowAddModal(false);
            setEditingHoliday(null);
          }}
        />
      )}

      {/* Philippine Presets Modal */}
      {canEdit && showPresetModal && (
        <PhilippinePresetsModal
          presets={philippinePresets}
          existingHolidays={holidays}
          companyRates={companyRates}
          onApply={handleApplyPresets}
          onClose={() => setShowPresetModal(false)}
        />
      )}
    </div>
  );
};

/**
 * Holiday Modal Component for Add/Edit
 */
const HolidayModal = ({ holiday, initialDate, companyRates, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: holiday?.name || initialDate?.name || "",
    type: holiday?.type || "regular",
    day: holiday?.day || initialDate?.day || 1,
    month: holiday?.month || initialDate?.month || 0,
    isRecurring: holiday?.isRecurring || false,
  });
  const titleId = useId();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  // Staging form — nothing is in flight while it is open, so busy stays false.
  return (
    <Modal isOpen onClose={onClose} labelledBy={titleId} className="modal-content holiday-modal">
        <div className="modal-header">
          <h3 id={titleId}>{holiday ? "Edit Holiday" : "Add Holiday"}</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              Holiday Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Company Foundation Day"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Holiday Type</label>
            <select
              className="form-input"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <option value="regular">Regular Holiday ({Math.round(companyRates.regularHolidayRate * 100)}% pay)</option>
              <option value="special">Special Holiday ({Math.round(companyRates.specialHolidayRate * 100)}% pay)</option>
              <option value="company">Company Holiday</option>
            </select>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Month</label>
              <select
                className="form-input"
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
              >
                {["January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"].map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Day</label>
              <input
                type="number"
                className="form-input"
                min="1"
                max="31"
                value={formData.day}
                onChange={(e) => setFormData({ ...formData, day: parseInt(e.target.value) })}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="settings-btn settings-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="settings-btn settings-btn-primary">
              {holiday ? "Update Holiday" : "Add Holiday"}
            </button>
          </div>
        </form>
    </Modal>
  );
};

/**
 * Philippine Presets Modal
 */
const PhilippinePresetsModal = ({ presets, existingHolidays, companyRates, onApply, onClose }) => {
  const [selectedPresets, setSelectedPresets] = useState([]);
  const titleId = useId();

  const regularHolidays = presets.filter(p => p.type === "regular" && p.isRecurring);
  const specialHolidays = presets.filter(p => p.type === "special" && p.isRecurring);

  const togglePreset = (presetName) => {
    setSelectedPresets(prev =>
      prev.includes(presetName)
        ? prev.filter(p => p !== presetName)
        : [...prev, presetName]
    );
  };

  const handleApply = () => {
    onApply(selectedPresets);
  };

  return (
    <Modal isOpen onClose={onClose} labelledBy={titleId} className="modal-content presets-modal">
        <div className="modal-header">
          <h3 id={titleId}>🇵🇭 Add Philippine Holidays</h3>
        </div>

        <div className="presets-content">
          <p className="presets-description">
            Select Philippine holidays to add to your calendar. These are based on official Philippine holidays.
          </p>

          <div className="presets-section">
            <h4>Regular Holidays ({Math.round(companyRates.regularHolidayRate * 100)}% pay)</h4>
            <div className="presets-list">
              {regularHolidays.map(preset => (
                <label key={preset.name} className="preset-item">
                  <input
                    type="checkbox"
                    checked={selectedPresets.includes(preset.name)}
                    onChange={() => togglePreset(preset.name)}
                  />
                  <span>{preset.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="presets-section">
            <h4>Special Non-Working Holidays ({Math.round(companyRates.specialHolidayRate * 100)}% pay)</h4>
            <div className="presets-list">
              {specialHolidays.map(preset => (
                <label key={preset.name} className="preset-item">
                  <input
                    type="checkbox"
                    checked={selectedPresets.includes(preset.name)}
                    onChange={() => togglePreset(preset.name)}
                  />
                  <span>{preset.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="settings-btn settings-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="settings-btn settings-btn-primary"
            onClick={handleApply}
            disabled={selectedPresets.length === 0}
          >
            Add {selectedPresets.length} Holiday{selectedPresets.length !== 1 ? "s" : ""}
          </button>
        </div>
    </Modal>
  );
};

export default HolidayCalendarPage;
